import { useState } from "react";

import {
  faRotateRight,
  faSparkles,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { first, last } from "lodash-es";

import { useCasePropertiesUpdatedTable } from "src/cases/caseView/history/events/CasePropertiesTable";
import {
  HistoryEntryGroup,
  HistoryGroupItem,
  HistorySingleEntry,
} from "src/cases/caseView/history/HistoryItemFrame";
import { InlineTable } from "src/cases/common/InlineTable";
import { useCase } from "src/cases/queries/cases";
import {
  CaseHistoryEvent,
  GroupableEventType,
  HistoryEntryActionGroup as HistoryEntryGroupType,
} from "src/cases/queries/casesTypes";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseCreationFlowInitiatedGroup: React.FC<{
  entry: HistoryEntryGroupType;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  "use memo";

  const { events } = entry;
  const [isExpanded, setIsExpanded] = useState(false);
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);

  const templateVersion = caseData?.data.template_version;

  // Extract updated_properties from action_completed event
  const completedEvent = events.find(
    (e) => e.event_data.event_type === "action_completed",
  );
  const updatedProperties =
    completedEvent?.event_data.event_type === "action_completed"
      ? completedEvent.event_data.updated_properties
      : undefined;

  const hasUpdatedProperties =
    updatedProperties && Object.keys(updatedProperties).length > 0;

  // Always call the hook unconditionally at the top level
  const { columns, rows } = useCasePropertiesUpdatedTable(
    updatedProperties || {},
    templateVersion,
  );

  const propertiesContent =
    hasUpdatedProperties && rows.length > 0 && isExpanded ? (
      <InlineTable columns={columns} data={rows} />
    ) : undefined;

  const initiatorEventData = last(events)?.event_data;

  if (!initiatorEventData) {
    return null;
  }

  if (events.length === 1) {
    const event = events[0];

    return (
      <HistorySingleEntry
        eventType={event.event_type}
        headline="Case is being prepared for investigation."
        icon={faSpinner}
        isLast={isLast}
        timestamp={event.timestamp}
      />
    );
  }

  const items = buildGroupItems(events, propertiesContent);

  return (
    <HistoryEntryGroup
      eventType="case_creation_flow_initiated"
      icon={getGroupIcon(events)}
      isExpanded={isExpanded}
      isLast={isLast}
      items={isExpanded ? items : [items[0]]}
      onToggle={setIsExpanded}
    />
  );
};

const buildGroupItems = (
  events: CaseHistoryEvent<GroupableEventType>[],
  propertiesContent: React.ReactNode,
) => {
  return events
    .map((event) => {
      const eventData = event.event_data;

      if (eventData.event_type === "action_completed") {
        return {
          timestamp: event.timestamp,
          eventType: event.event_type,
          headline: "Case was enriched and prepared for investigation.",
          content: propertiesContent,
        };
      }

      if (eventData.event_type === "action_failed") {
        return {
          timestamp: event.timestamp,
          eventType: event.event_type,
          headline:
            "Case preparation could not be completed due to an error on the initiation flow.",
        };
      }

      if (eventData.event_type === "case_creation_flow_initiated") {
        if (eventData.retry) {
          return {
            timestamp: event.timestamp,
            eventType: event.event_type,
            headline: "Case Preparation Flow was retried.",
            actorId: eventData.retried_by,
          };
        }
        return {
          timestamp: event.timestamp,
          eventType: event.event_type,
          headline: "Case is being prepared for investigation.",
        };
      }
    })
    .filter(Boolean) as HistoryGroupItem[];
};

const getGroupIcon = (events: CaseHistoryEvent<GroupableEventType>[]) => {
  const firstEvent = first(events);
  const eventData = firstEvent?.event_data;

  if (
    eventData?.event_type === "case_creation_flow_initiated" &&
    eventData.retry
  ) {
    return faRotateRight;
  }

  if (eventData?.event_type === "action_failed") {
    return faTriangleExclamation;
  }

  return faSparkles;
};
